import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { SelectChangeEvent } from '@mui/material';
import { Box, Skeleton, Typography } from '@mui/material';
import type { MouseEvent } from 'react';
import { memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { SelectFlows } from 'features/cabinet/facebook';
import type { AdSetEntry } from '../api/getAdSets';
import { isAdSetFlowEmpty, updateAdSetFlowInCache, updateAdSetFlowsInCache, } from '../utils/adSetFlowCache';
import { AD_SETS_STEP_INDEX, adSetRequiresFlowSelect, } from '../validation/validateSelectedAdSetsFlows';
import { AdSetApplyToAllButton } from './AdSetApplyToAllButton';

interface AdSetFlowFieldProps {
  cellParams: GridRenderCellParams<AdSetEntry>;
}

const stopPropagation = (e: MouseEvent) => {
  e.stopPropagation();
};

const FlowSelectFallback = (
  <Skeleton
    width="100%"
    height={40}
    sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
  />
);

export const AdSetFlowField = ({ cellParams }: AdSetFlowFieldProps) => {
  const currentId = cellParams.row.id;
  const rowFlow = cellParams.row.flow;
  const rowFlowAssignedByUser = cellParams.row.flowAssignedByUser;
  const { watch, setValue, getValues } = useFormContext();
  const queryClient = useQueryClient();

  const selectedAdSets: AdSetEntry[] = watch('selectedAdSets');
  const fieldName = `selectedAdSetsFlows.${currentId}`;
  const userFlow: string | undefined = watch(fieldName);
  const app = watch('app') as string | undefined;
  const currentStep = watch('currentStep') as number | undefined;
  const isFlowRequired = currentStep === AD_SETS_STEP_INDEX;

  const selectedAdSet = useMemo(
    () => selectedAdSets?.find((adSet) => adSet.id === currentId),
    [selectedAdSets, currentId],
  );

  const isSelected = !!selectedAdSet;

  const hasUserFlow = !isAdSetFlowEmpty(userFlow);
  const assignedFlow = selectedAdSet?.flow ?? rowFlow;
  const flowAssignedByUser =
    hasUserFlow ||
    Boolean(selectedAdSet?.flowAssignedByUser) ||
    Boolean(rowFlowAssignedByUser);
  const flow = hasUserFlow ? userFlow : assignedFlow;

  const shouldShowSelect =
    isAdSetFlowEmpty(assignedFlow) || flowAssignedByUser;

  useEffect(() => {
    if (!isSelected || !flowAssignedByUser || isAdSetFlowEmpty(assignedFlow)) {
      return;
    }

    if (!isAdSetFlowEmpty(userFlow)) {
      return;
    }

    setValue(fieldName, assignedFlow, { shouldValidate: false });
  }, [
    isSelected,
    flowAssignedByUser,
    assignedFlow,
    userFlow,
    fieldName,
    setValue,
  ]);

  const handleFlowChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const nextFlow = String(event.target.value ?? '');

      updateAdSetFlowInCache(queryClient, currentId, nextFlow);

      const currentFlows: Record<string, string> = getValues('selectedAdSetsFlows') ?? {};
      setValue(
        'selectedAdSetsFlows',
        {
          ...currentFlows,
          [currentId]: nextFlow,
        },
        { shouldValidate: true },
      );

      const currentSelected: AdSetEntry[] = getValues('selectedAdSets') ?? [];
      setValue(
        'selectedAdSets',
        currentSelected.map((adSet) =>
          adSet.id === currentId
            ? { ...adSet, flow: nextFlow, flowAssignedByUser: true }
            : adSet,
        ),
        { shouldValidate: true },
      );
    },
    [queryClient, currentId, getValues, setValue],
  );

  const showApplyToAll = (selectedAdSets?.length ?? 0) > 1;
  const canApplyToAll = shouldShowSelect && !isAdSetFlowEmpty(flow);

  const handleApplyToAll = useCallback(() => {
    const flowToApply: string | undefined = getValues(fieldName);
    if (!flowToApply) return;

    const currentSelected: AdSetEntry[] = getValues('selectedAdSets') ?? [];
    const targetIds = currentSelected
      .filter(adSetRequiresFlowSelect)
      .map((adSet) => adSet.id);

    if (!targetIds.length) return;

    const flowsByAdSetId: Record<string, string> = Object.fromEntries(
      targetIds.map((id) => [id, flowToApply]),
    );

    updateAdSetFlowsInCache(queryClient, flowsByAdSetId);

    const currentFlows: Record<string, string> = getValues('selectedAdSetsFlows') ?? {};
    setValue(
      'selectedAdSetsFlows',
      { ...currentFlows, ...flowsByAdSetId },
      { shouldValidate: true },
    );

    setValue(
      'selectedAdSets',
      currentSelected.map((adSet) =>
        Object.hasOwn(flowsByAdSetId, adSet.id)
          ? { ...adSet, flow: flowToApply, flowAssignedByUser: true }
          : adSet,
      ),
      { shouldValidate: true },
    );
  }, [fieldName, getValues, setValue, queryClient]);

  if (!isSelected) {
    return null;
  }

  if (!shouldShowSelect) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography variant="body2" noWrap title={flow ?? undefined}>
          {flow}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        height: '100%',
        gap: 1,
        flexWrap: 'nowrap',
        alignItems: 'center',
        width: '100%',
      }}
      onClick={stopPropagation}
    >
      <Suspense fallback={FlowSelectFallback}>
        <SelectFlows
          key={currentId}
          asAutocomplete
          size="small"
          valueKey="name"
          name={fieldName}
          inputLabel=""
          placeholder={
            isEmpty(app) ? 'Please select an app first' : 'Select a flow'
          }
          disabled={isEmpty(app)}
          params={{ apps: app ? [app] : undefined }}
          handleChange={handleFlowChange}
          hideErrorMessage
          required={isFlowRequired}
          sx={{
            root: {
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 120,
            },
          }}
        />
      </Suspense>
      {showApplyToAll && (
        <AdSetApplyToAllButton
          onClick={handleApplyToAll}
          testId="applyToAllFlowButton"
          ariaLabel="Apply flow to all selected ad sets"
          disabled={!canApplyToAll}
        />
      )}
    </Box>
  );
};

const arePropsEqual = (
  prevProps: AdSetFlowFieldProps,
  nextProps: AdSetFlowFieldProps,
): boolean => {
  const prevRow = prevProps.cellParams.row;
  const nextRow = nextProps.cellParams.row;

  return (
    prevRow.id === nextRow.id &&
    prevRow.flow === nextRow.flow &&
    prevRow.flowAssignedByUser === nextRow.flowAssignedByUser &&
    prevProps.cellParams.hasFocus === nextProps.cellParams.hasFocus
  );
};

export const MemoizedAdSetFlowField = memo(AdSetFlowField, arePropsEqual);
